import React, { useCallback, useEffect, useState } from 'react';
import SubmissionTable from './SubmissionTable';
import Pagination from 'reusable/pagination';
import Toolbar from './Toolbar';
import axiosInstance from 'config/axiosInstance';
import { businessDateRange, businessZoneQuery, toBusinessDate } from 'config/time';
import { useTranslation } from 'react-i18next';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';
import { pageToRecover } from 'reusable/pageRecovery';

interface Record {
  id: number;
  name: string;
  phone: string;
  email: string;
  alcoholtests: {
    id: number;
    data: string;
  }[];
  profile_picture?: string;
}

const SubmissionRecent: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const rowsPerPage = 10;
  const [count, setCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('');
  // ADM-05。日付は日本時間の業務日。UTCの日付にすると 0〜9時は前日になる。
  const [dateValue, setDateValue] = useState(toBusinessDate(new Date()));
  const [currentPage, setCurrentPage] = useState(1);
  const [records, setRecords] = useState<Record[]>([]);
  // ADM-15。取得の失敗（空の一覧と区別する）と、最後の要求の応答だけを反映するための番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();

  // 認証（access の付与・Cookie での取り直し）は axiosInstance が行う（ADM-17）。
  const fetchProfilePicture = async (id: number) => {
    try {
      const response = await axiosInstance.get(`/v1/drivers/${id}/`);
      return response.data.profile_picture;
    } catch (error) {
      console.error(`Failed to fetch profile picture for driver ${id}`, error);
      return null;
    }
  };

  const fetchData = useCallback(async () => {
    const isCurrent = beginRequest();
    const { gte, lte } = businessDateRange(dateValue, dateValue);
    // RV-278・RV-279。画面の境界の計算と同じ時間帯を time_zone で渡す。
    const apiUrl = `/v1/drivers/recent_submission/?${businessZoneQuery()}alcoholtest__testing_time__gte=${encodeURIComponent(
      gte ?? ''
    )}&alcoholtest__testing_time__lte=${encodeURIComponent(
      lte ?? ''
    )}&order_by=${sortValue}&search=${searchValue}&page=${currentPage}`;

    try {
      const response = await axiosInstance.get(apiUrl);
      if (!isCurrent()) return;
      // EV-460。今のページが無くなっていたら（空のページ・件数あり）、最後の有効なページを読む。
      const recover = pageToRecover(currentPage, rowsPerPage, {
        count: response.data.count,
        rows: response.data.results?.length ?? 0,
      });
      if (recover !== null) {
        setCurrentPage(recover);
        return;
      }
      const fetchedRecords = response.data.results;

      const updatedRecords = await Promise.all(
        fetchedRecords.map(async (record: Record) => {
          const profilePicture = await fetchProfilePicture(record.id);
          return { ...record, profile_picture: profilePicture };
        })
      );

      if (!isCurrent()) return;
      setRecords(updatedRecords);
      setCount(response.data.count);
      setFetchError(null);
    } catch (error) {
      if (!isCurrent()) return;
      // EV-460。ページの外（404）なら、エラーにせず1ページ目を読む。
      const recoverPage = pageToRecover(currentPage, rowsPerPage, { error });
      if (recoverPage !== null) {
        setCurrentPage(recoverPage);
        return;
      }
      console.error('Failed to fetch data', error);
      setFetchError(error);
    }
  }, [searchValue, sortValue, dateValue, currentPage, beginRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleSortChange = (value: string) => {
    setSortValue(value);
    setCurrentPage(1);
  };

  const handleDateChange = (value: string) => {
    setDateValue(value);
    setCurrentPage(1);
  };

  const handlePageChange = (value: number) => {
    setCurrentPage(value);
  };

  return (
    <div className="flex flex-col bg-white rounded-md p-4">
      {/* Toolbar */}
      <div className="flex flex-row justify-between items-center">
        {/* 2026-11-03 UAT A30（和田様）。「当日の測定状況」とし、管理者の確認用であることを添える。 */}
        <div className="flex flex-col">
          <h4 className="text-subtitle-1 font-medium">{t('table.title')}</h4>
          <p className="text-caption text-neutral/300">{t('table.lead')}</p>
        </div>
        <Toolbar
          searchValue={searchValue}
          sortValue={sortValue}
          dateValue={dateValue}
          currentPage={currentPage}
          onSearchChange={handleSearchChange}
          onSortChange={handleSortChange}
          onDateChange={handleDateChange}
          onPageChange={handlePageChange}
        />
      </div>
      {/* Table */}
      {fetchError ? (
        <FetchFailed error={fetchError} onRetry={fetchData} />
      ) : (
        <SubmissionTable records={records} />
      )}
      {/* Pagination Section */}
      <Pagination
        allRows={count}
        rowsPerPage={rowsPerPage}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default SubmissionRecent;